import qs from 'qs'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { deleteWashingMethod, getAllWashingMethod, getWashingMethod, getWashingMethodsApi } from '@/apis/washingMethods'
import { API_LIMIT } from '@/constants/api'
import useNotification from '@/hooks/useNotification'
import useQuery from '@/hooks/useQuery'
import useQueryPage from '@/hooks/useQueryPage'
import { WashingMethod } from '@/types'
import { DeleteIdCardRequest } from '@/types/apis/idCards'
import { GetAllWashingMethodResponse } from '@/types/apis/washingMethods/GetAllWashingMethodResponse'
import { GetWashingMethodRequest } from '@/types/apis/washingMethods/GetWashingMethodRequest'

export type PageQueryParams = {
  page?: number
}

export const useWashingMethods = () => {
  const { t } = useTranslation()
  const openNotification = useNotification()
  const currentPage = useQueryPage()
  const query = useQuery()
  const [page, setPage] = useState(currentPage)
  const [getWashingMethodLoading, setGetWashingMethodsLoading] = useState(false)
  const [getWashingMethodsResponse, setWashingMethodsResponse] = useState<GetAllWashingMethodResponse>()
  const [deleteWashingMethodLoading, setDeleteWashingMethodLoading] = useState(false)
  const [getWashingMethodDetailLoading, setWashingMethodDetailLoading] = useState(false)
  const [getWashingMethodDetailResponse, setWashingMethodDetailResponse] = useState<WashingMethod>()
  const [washingMethods, setWashingMethods] = useState<WashingMethod[]>([])

  const filterToQuery = (filter: { page?: number }): string => {
    const filterQuery: PageQueryParams = {}

    if (filter.page) {
      filterQuery.page = filter.page
    }

    return qs.stringify(filterQuery)
  }

  const get = useCallback(async () => {
    try {
      setGetWashingMethodsLoading(true)
      const res = await getAllWashingMethod({
        limit: API_LIMIT,
        page,
      })

      setWashingMethodsResponse(res)
    } finally {
      setGetWashingMethodsLoading(false)
    }
  }, [page])

  const getAllWashingMethods = useCallback(async () => {
    try {
      setGetWashingMethodsLoading(true)
      const res = await getWashingMethodsApi()

      setWashingMethods(res)
    } finally {
      setGetWashingMethodsLoading(false)
    }
  }, [])

  const del = useCallback(
    async ({ id }: DeleteIdCardRequest) => {
      try {
        setDeleteWashingMethodLoading(true)
        await deleteWashingMethod({ id })

        if (getWashingMethodsResponse) {
          const newWashingMethods = getWashingMethodsResponse.items.filter((item) => item.id !== id)
          setWashingMethodsResponse({ ...getWashingMethodsResponse, ...newWashingMethods })
        }
        openNotification({ type: 'success', description: t('components.pages.admins.common.delete.success') })
      } catch {
        openNotification({ type: 'error', description: t('components.pages.admins.common.delete.fail') })
      } finally {
        setDeleteWashingMethodLoading(false)
      }
    },
    [getWashingMethodsResponse, openNotification, t]
  )

  const getDetail = useCallback(async (request: GetWashingMethodRequest) => {
    try {
      setWashingMethodDetailLoading(true)
      const res = await getWashingMethod(request)
      setWashingMethodDetailResponse(res)
    } finally {
      setWashingMethodDetailLoading(false)
    }
  }, [])

  useEffect(() => {
    const page = query.get('page')
    setPage(page ? Number(page) : 1)
  }, [query])

  return {
    page,
    pageSize: API_LIMIT,
    setPage,
    filterToQuery,
    getWashingMethods: get,
    getWashingMethodsResponse,
    getWashingMethodLoading,
    deleteWashingMethod: del,
    deleteWashingMethodLoading,
    getWashingMethodDetail: getDetail,
    getWashingMethodDetailResponse,
    getWashingMethodDetailLoading,
    getAllWashingMethods,
    washingMethods,
  }
}
